/* eslint-disable no-template-curly-in-string */
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  SwipeableDrawer,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AddIcon,
  AddVideoIcon,
  CancelIcon,
  CorrectIcon,
  DeleteForeverIcon,
  EditIcon,
  TranscribeIcon,
  UploadIcon,
} from 'src/v3/core/icons'

import * as yup from 'yup'
import pLimit from 'p-limit'
import { compose, F, T } from 'ramda'
import { ReactElement, useEffect, useState } from 'react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchClasses,
  selectActiveClass,
  selectClassByID,
  selectClassesStatus,
  fetchVideos,
  selectVideosByClassID,
  selectVideosStatus,
} from 'src/v3/create/create.slice'

import theme from 'src/theme'
import { useFormik, FormikProvider } from 'formik'
import axios from 'src/utilities/http-client'
import ConfirmAction from 'src/v3/components/confirm-action'
import Loading from 'src/v3/components/loading'
import { selectUseTranscriptEditor } from 'src/v3/settings/settings.slice'

type Part = {
  etag: string
  partNumber: number
}

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let classesStatus = useAppSelector(selectClassesStatus)
  let activeClass = useAppSelector(selectActiveClass)
  let classData = useAppSelector(selectClassByID(activeClass))

  let videos = useAppSelector(selectVideosByClassID(activeClass))
  let videosStatus = useAppSelector(selectVideosStatus)

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [formOpen, setFormOpen] = useState<boolean>(false)
  let [saving, setSaving] = useState<boolean>(false)
  let [deleting, setDeleting] = useState<string>('')
  let [file, setFile] = useState<File | null>(null)
  let [uploadVideoID, setUploadVideoID] = useState<string>('')
  let [uploading, setUploading] = useState<number>(0)
  let [transcriptAvailability, setTranscriptAvailability] = useState<
    Record<string, boolean>
  >({})
  let [transcribing, setTranscribing] = useState<Record<string, boolean>>({})
  let [transcribeConfirmOpen, setTranscribeConfirmOpen] = useState<string>('')
  let useTranscriptEditor = useAppSelector(selectUseTranscriptEditor)

  let uploadVideoData = videos.find((v) => v.id === uploadVideoID)

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      description: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      description: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post('/api/v2/create/video', {
            name: values.name,
            description: values.description,
            key: '',
            classID: activeClass,
          })
          .then(() => dispatch(fetchVideos()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Video')
            dispatch(fetchVideos())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/create/video', {
            id: values.id,
            name: values.name,
            description: values.description,
          })
          .then(() => dispatch(fetchVideos()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Video')
            dispatch(fetchVideos())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (videosStatus === 'uninitialized') {
      dispatch(fetchVideos())
    }
  }, [videosStatus])

  useEffect(() => {
    if (videosStatus === 'fulfilled') {
      let fetchAllTranscripts = async () => {
        let results = await Promise.all(
          videos.map(async (video) => {
            try {
              // Get the transcript metadata
              let response = await axios.get(
                `/api/create/video/${video.id}/transcript`,
              )

              // Verify that we can actually access the transcript in S3
              try {
                let s3Url = response.data
                if (s3Url) {
                  await axios.get(s3Url)
                  return { id: video.id, hasTranscript: true }
                }
                return { id: video.id, hasTranscript: false }
              } catch (s3Error) {
                return { id: video.id, hasTranscript: false }
              }
            } catch (error) {
              return { id: video.id, hasTranscript: false }
            }
          }),
        )

        // Convert array of results to Record<string, boolean>
        let transcriptStatus = results.reduce(
          (acc, { id, hasTranscript }) => {
            // eslint-disable-next-line fp/no-mutation
            acc[id] = hasTranscript
            return acc
          },
          {} as Record<string, boolean>,
        )

        setTranscriptAvailability(transcriptStatus)
      }

      fetchAllTranscripts()
    }
  }, [videosStatus])

  useEffect(() => {
    if (activeClass === 'unselected') {
      navigate('/create')
    }
  }, [activeClass])

  let handleFileChange = (e: React.FormEvent<HTMLInputElement>) => {
    setFile(e.currentTarget.files?.[0] || null)
  }

  let getPresignedUrls = async (numberOfParts: number) => {
    let response = await axios.get(
      `/api/video-multipart-urls?numberOfParts=${numberOfParts}`,
    )
    return response.data
  }

  let uploadPart = async (presignedUrl: string, part: Blob) => {
    let response = await axios.put(presignedUrl, part, {
      headers: {
        'Cache-Control': 'max-age=2592000',
        'content-type': 'video/mp4',
      },
    })
    return response.headers
  }

  let completeMultipartUpload = async (
    key: string,
    uploadId: string,
    parts: Part[],
  ) => {
    await axios.post('/api/complete-video-multipart-upload', {
      key,
      uploadId,
      parts,
    })
  }

  let handleUpload = async () => {
    if (!file) return

    let numberOfParts = Math.floor(file!.size / 5242880) || 1
    let { signedURLs, uploadID, key } = await getPresignedUrls(numberOfParts)

    let partSize = Math.ceil(file!.size / numberOfParts)
    let parts: Part[] = []
    let completedParts = 0

    let uploadPartWithProgress = async (
      url: string,
      part: Blob,
      partNumber: number,
    ) => {
      let response = await uploadPart(url, part)
      // eslint-disable-next-line fp/no-mutating-methods
      parts.push({ etag: response.etag, partNumber })
      // eslint-disable-next-line fp/no-mutation
      completedParts += 1
      setUploading((100 * completedParts) / (numberOfParts + 1))
    }

    let limit = pLimit(6)

    let uploadPromises = Array.from({ length: numberOfParts }, (_, i) => {
      let part = file!.slice(i * partSize, (i + 1) * partSize)
      return limit(() => uploadPartWithProgress(signedURLs[i], part!, i + 1))
    })

    await Promise.all(uploadPromises)

    await completeMultipartUpload(key, uploadID, parts)
    await axios.put('/api/v2/create/video/file', {
      id: uploadVideoID,
      fileName: key,
    })

    setUploading(100)
    dispatch(fetchVideos())
  }

  let onEdit = (id: string) => {
    let v = videos.find((x) => x.id === id)
    form.setFieldValue('id', v?.id)
    form.setFieldValue('name', v?.name)
    form.setFieldValue('description', v?.description)
    setFormOpen(true)
  }

  let resetVideoUpload = () => {
    setUploading(0)
    setUploadVideoID('')
    setFile(null)
  }

  let handleTranscribe = async (videoId: string) => {
    setTranscribing({ ...transcribing, [videoId]: true })

    try {
      await axios.post(`/api/create/video/${videoId}/transcript/trigger`)

      // Poll for transcript completion
      let pollCount = 0
      let maxPollCount = 60 // 10 minutes at 10 second intervals

      let pollInterval = setInterval(async () => {
        try {
          // eslint-disable-next-line fp/no-mutation, no-plusplus
          pollCount++
          let response = await axios.get(
            `/api/create/video/${videoId}/transcript`,
          )

          if (response.status === 200 && response.data) {
            // Check if we can access the transcript
            try {
              await axios.get(response.data)

              // Reinit transcript availability
              setTranscriptAvailability((prev) => ({
                ...prev,
                [videoId]: true,
              }))
              setTranscribing({ ...transcribing, [videoId]: false })
              clearInterval(pollInterval)
            } catch {
              // S3 file not ready yet, continue polling
              if (pollCount >= maxPollCount) {
                setErrorMessage(
                  'Transcription timed out. Please try again later.',
                )
                setTranscribing({ ...transcribing, [videoId]: false })
                clearInterval(pollInterval)
              }
            }
          } else if (pollCount >= maxPollCount) {
            setErrorMessage('Transcription timed out. Please try again later.')
            setTranscribing({ ...transcribing, [videoId]: false })
            clearInterval(pollInterval)
          }
        } catch (error) {
          if (pollCount >= maxPollCount) {
            setErrorMessage('Transcription failed. Please try again later.')
            setTranscribing({ ...transcribing, [videoId]: false })
            clearInterval(pollInterval)
          }
        }
      }, 10000) // Poll every 10 seconds
    } catch (error) {
      setErrorMessage('Failed to trigger transcription. Please try again.')
      setTranscribing({ ...transcribing, [videoId]: false })
    }
  }

  return (
    <>
      <Box component="main" sx={{ p: 3 }}>
        <Typography
          variant="h4"
          className="my-4 d-inline"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Videos
        </Typography>
        <Typography paragraph className="ml-3 d-inline">
          Create and edit videos for {classData?.name}
        </Typography>
      </Box>
      <Box sx={{ px: 3 }}>
        <Button
          sx={{ mb: 2 }}
          variant="outlined"
          size="large"
          startIcon={<AddIcon />}
          disabled={activeClass === 'unselected'}
          onClick={compose(setFormOpen, T)}
        >
          Add Video
        </Button>
        <Grid container spacing={2}>
          {videos.map((v) => (
            <Grid key={v.id} item xl={4} md={6} xs={12}>
              <Card>
                <CardHeader
                  sx={{ flexWrap: 'wrap', rowGap: 1 }}
                  avatar={
                    <Avatar
                      sx={{ backgroundColor: theme.palette.primary.main }}
                    >
                      {v.name[0]}
                    </Avatar>
                  }
                  title={
                    <Button
                      component={RouterLink}
                      to={`/create/videos/video/${v.id}`}
                      sx={{ textAlign: 'left', textTransform: 'none', pl: 0 }}
                    >
                      {v.name}
                    </Button>
                  }
                  titleTypographyProps={{ variant: 'h6', sx: { pr: 2 } }}
                  subheader={v.description}
                  subheaderTypographyProps={{ sx: { pr: 2 } }}
                />
                {v.fileName !==
                '${import.meta.env.VITE_CLOUDFRONT_URL}/public/' ? (
                  <CardMedia
                    sx={{ px: 2 }}
                    component="video"
                    image={v.fileName}
                    controls
                  />
                ) : (
                  <Typography sx={{ px: 2 }}>
                    You have not yet attached a file to this video. Please
                    upload a file to complete creating your video content.
                  </Typography>
                )}
                <Box
                  sx={{
                    p: 2,
                    pt: 1,
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Tooltip title="Upload Video">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddVideoIcon />}
                      onClick={() => setUploadVideoID(v.id)}
                    >
                      Upload
                    </Button>
                  </Tooltip>
                  <Tooltip title="Edit Video">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEdit(v.id)}
                    >
                      Edit
                    </Button>
                  </Tooltip>
                  {useTranscriptEditor && (
                    <>
                      <Tooltip
                        title={
                          transcriptAvailability[v.id] === false
                            ? 'No transcript available for this video'
                            : 'Edit video transcript'
                        }
                      >
                        <span>
                          <Button
                            id={`video-editor-button-${v.id}`}
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              navigate(`/create/videos/video/${v.id}`)
                            }
                            disabled={
                              transcriptAvailability[v.id] === false ||
                              transcribing[v.id]
                            }
                          >
                            Edit Transcript
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          transcriptAvailability[v.id]
                            ? 'A transcript already exists'
                            : 'Manually trigger transcription'
                        }
                      >
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                              transcribing[v.id] ? undefined : (
                                <TranscribeIcon />
                              )
                            }
                            onClick={() => setTranscribeConfirmOpen(v.id)}
                            color={
                              !!transcriptAvailability[v.id] ||
                              transcribing[v.id]
                                ? 'error'
                                : 'primary'
                            }
                          >
                            {transcribing[v.id]
                              ? 'Transcribing...'
                              : 'Transcribe'}
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Delete Video">
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<DeleteForeverIcon />}
                      onClick={() => setDeleting(`delete-${v.id}`)}
                    >
                      Delete
                    </Button>
                  </Tooltip>
                  <ConfirmAction
                    executeAction={() =>
                      axios
                        .delete(`/api/v2/create/delete-video?videoID=${v.id}`)
                        .then(() => dispatch(fetchVideos()))
                    }
                    open={deleting}
                    setOpen={setDeleting}
                    openText={`delete-${v.id}`}
                    title="Confirm Deletion"
                    text={`Are you sure you want to delete ${v.name}?`}
                    onFailure={(err) =>
                      setErrorMessage(err.message || 'Error Deleting Video')
                    }
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        <SwipeableDrawer
          anchor="bottom"
          open={formOpen && !saving}
          onOpen={compose(setFormOpen, T)}
          onClose={compose(setFormOpen, F)}
          style={{ zIndex: 2000 }}
        >
          <Container maxWidth="lg">
            <Card className="m-4 overflow-auto vh-full">
              <FormikProvider value={form}>
                <form className="p-4" onSubmit={form.handleSubmit}>
                  <TextField
                    fullWidth
                    className="mt-4"
                    variant="standard"
                    value={form.values.name}
                    label="Name"
                    name="name"
                    onChange={form.handleChange}
                    error={form.touched.name && !!form.errors.name}
                  />
                  <TextField
                    fullWidth
                    className="my-4"
                    variant="standard"
                    value={form.values.description}
                    label="Description"
                    name="description"
                    onChange={form.handleChange}
                    error={
                      form.touched.description && !!form.errors.description
                    }
                  />
                  <div className="flex flex-row-reverse">
                    <Button
                      variant="contained"
                      color="primary"
                      className="mr-3"
                      type="submit"
                    >
                      Submit
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      className="mr-3"
                      onClick={compose(setFormOpen, F)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </FormikProvider>
            </Card>
          </Container>
        </SwipeableDrawer>
      </Box>
      <Dialog open={!!uploadVideoID} onClose={() => resetVideoUpload()}>
        <Paper className="px-3 py-1" sx={{ width: 300 }}>
          <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
            {`${uploadVideoData?.name} Video Upload`}
          </DialogTitle>
          <DialogContent sx={{ px: 1, pb: 1 }}>
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <input
                  accept="video/mp4"
                  style={{ display: 'none' }}
                  id="raised-button-file"
                  multiple
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="raised-button-file">
                  <Button color="primary" variant="outlined" component="span">
                    Select Video
                  </Button>
                </label>
              </Box>
              {file && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <CorrectIcon color="success" />
                  <Typography>{file.name}</Typography>
                </Box>
              )}
            </>
          </DialogContent>
          <DialogActions className="flex justify-center pt-0 pb-3 mt-0">
            {uploading ? (
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" align="center" sx={{ mb: 1 }}>
                  Your file is uploading. This will take a moment.
                </Typography>
                <LinearProgress variant="determinate" value={uploading} />
              </Box>
            ) : (
              <>
                <Button
                  onClick={() => {
                    setUploading(0.1)
                    handleUpload()
                      .then(() => resetVideoUpload())
                      .catch((err) => {
                        setUploading(0)
                        setErrorMessage(err?.message || 'Error Uploading Video')
                      })
                  }}
                  color="primary"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<UploadIcon />}
                  disabled={!file}
                >
                  Upload
                </Button>
                <Button
                  onClick={resetVideoUpload}
                  color="error"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
              </>
            )}
          </DialogActions>
        </Paper>
      </Dialog>
      <Dialog
        open={!!transcribeConfirmOpen}
        onClose={() => setTranscribeConfirmOpen('')}
      >
        <DialogTitle>Confirm Manual Transcription</DialogTitle>
        <DialogContent>
          <Typography>
            Videos are automatically transcribed upon upload. If this is a newly
            uploaded video, there is no need to manually transcribe it. Please
            only use this for older videos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranscribeConfirmOpen('')}>Cancel</Button>
          <Button
            onClick={() => {
              handleTranscribe(transcribeConfirmOpen)
              setTranscribeConfirmOpen('')
            }}
            variant="contained"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[classesStatus, videosStatus]}
        loading={saving || Object.values(transcribing).some(Boolean)}
      />
    </>
  )
}
